import * as React from "react";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker, useNavigate } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { getValidator } from "./validator";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultSecretInput, inputToSecret } from "../utils";
import { SecretInput } from "../../../schema/types/secret/input";
import { SecretCreateContent } from "./content";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { secretApi } from "../../../store/api/secretApi";

interface Props {}
export const SecretCreateForm: React.FC<Props> = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultSecretInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/secret/-create`) &&
      !nextLocation.pathname.includes(`/secret/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: SecretInput) {
    try {
      let body = inputToSecret(input);
      const method = "post";
      const url = homeLink("secret");

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("secret", body.name);
      dispatch(secretApi.util.invalidateTags([{ type: "Secret", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Secret is created",
      });
      dispatch(setRedirect(`/console${itemLink}`));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <SecretCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal inputToItemFn={inputToSecret} kind="secret" namePath="name" title="Secret" />
      <CpDebugModal inputToItemFn={inputToSecret} kind="secret" namePath="name" title="Secret" />
    </CpFormContext.Provider>
  );
};
